import React, { useRef, useEffect } from "react";
import { RinkMap } from "./rinkPlot_pierreanalytics"; // Adjust path if needed
import * as d3 from "d3";

export default function RinkSVG({ width = 900, height = 400, showDanger = false, fullRink = true, watermark = "", shots = [] }) {
  const containerRef = useRef();

  useEffect(() => {
    d3.select(containerRef.current).selectAll("*").remove();

    // Create SVG element with viewBox
    const svg = d3.select(containerRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    // Call the RinkMap function with config (use only props, no hardcoded values)
    const rink = RinkMap({
      parent: svg,
      desiredWidth: width,
      chartsize: { width, height },
      showDanger,
      fullRink,
      horizontal: true,
      watermark,
    });
    rink.chart();

    // --- Plot shots as SVG circles ---
    const rinkLengthFt = 200;
    const rinkWidthFt = 85;
    const scale = Math.min(width / rinkLengthFt, height / rinkWidthFt);
    const offsetX = (width - rinkLengthFt * scale) / 2;
    const offsetY = (height - rinkWidthFt * scale) / 2;

    // Group for shots
    const shotsGroup = svg.append("g").attr("class", "shots-group");

    // Tooltip state (only one at a time)
    let tooltipDiv = d3.select(containerRef.current).select(".svg-tooltip");
    if (!tooltipDiv.node()) {
      tooltipDiv = d3.select(containerRef.current)
        .append("div")
        .attr("class", "svg-tooltip")
        .style("position", "absolute")
        .style("pointer-events", "none")
        .style("z-index", 1000)
        .style("display", "none");
    }

    shots.forEach((shot, idx) => {
      const svgX = (shot.x + 100) * scale + offsetX;
      const svgY = (42.5 - shot.y) * scale + offsetY;
      const fill = shot.color || (shot.goal ? "#ff3366" : "#888");
      const stroke = shot.border || "#222";
      const r = shot.goal ? 10 : 7;

      // Draw circle
      const shotCircle = shotsGroup.append("circle")
        .attr("cx", svgX)
        .attr("cy", svgY)
        .attr("r", r)
        .attr("fill", fill)
        .attr("stroke", stroke)
        .attr("stroke-width", shot.goal ? 4 : 2)
        .attr("opacity", 0.85)
        .attr("class", shot.goal ? "shot-goal" : "shot-attempt")
        .style("cursor", "pointer")
        .on("mouseenter", function (event) {
          // Conditionally set width for goal tooltips
          const widthStyle = shot.goal === 1
            ? 'min-width:200px;max-width:600px;'
            : 'min-width:315px;max-width:455px;';
          tooltipDiv
            .html(`
              <div class=\"glass-tooltip\" style=\"${widthStyle}\">
                <div class=\"tooltip-content\">
                  ${shot.goal === 1
                    ? `<em>${shot.strength_str} Goal!</em><br>${shot.shot_type}<br><b>${shot.xg_value} xG</b><br>Scored by <strong>${shot.shooter_name}</strong><br>${shot.assist_str}<br>${shot.time_remaining} remaining in period ${shot.period}`
                    : shot.sog === 1
                      ? `<b>${shot.xg_value} xG</b><br>Shot on goal by ${shot.shooter_name}<br>${shot.time_remaining} remaining in period ${shot.period}`
                      : `<b>${shot.xg_value} xG</b><br>Shot attempt by ${shot.shooter_name}<br>${shot.time_remaining} remaining in period ${shot.period}`
                  }
                  <img src=\"https://assets.nhle.com/mugs/nhl/${shot.season}/${shot.playing_for}/${shot.shooter_id}.png\" style=\"width:75px;height:auto;margin-top:8px;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.15);\"/>
                </div>
              </div>
            `)
            .style("display", "block");
          // Position tooltip so its bottom border is at the cursor (shot circle)
          const tooltipNode = tooltipDiv.node();
          tooltipDiv.style("visibility", "hidden").style("left", `0px`).style("top", `0px`);
          const tooltipRect = tooltipNode.getBoundingClientRect();
          const tooltipWidth = tooltipRect.width || 0;
          const tooltipHeight = tooltipRect.height || 0;
          tooltipDiv.style("visibility", "visible");
          tooltipDiv
            .style("left", `${svgX - tooltipWidth / 2}px`)
            .style("top", `${svgY - r - tooltipHeight}px`);
        })
        .on("mouseleave", function () {
          tooltipDiv.style("display", "none");
        });
    });

    // Clean up on unmount
    return () => {
      d3.select(containerRef.current).selectAll("*").remove();
    };
  }, [width, height, showDanger, fullRink, watermark, shots]);

  return <div ref={containerRef} style={{position: 'relative'}} />;
}

// Add glassmorphism tooltip styles
if (typeof window !== 'undefined' && !document.getElementById('glass-tooltip-style')) {
  const style = document.createElement('style');
  style.id = 'glass-tooltip-style';
  style.innerHTML = `
    .glass-tooltip {
      background: rgba(255,255,255,0.25);
      box-shadow: 0 8px 32px 0 rgba(31,38,135,0.37);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,0.18);
      padding: 18px 20px 12px 20px;
      min-width: 315px;
      max-width: 455px;
      color: #1a2746;
      font-size: 16px;
      font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
      text-align: center;
      z-index: 10000;
      pointer-events: none;
      transition: box-shadow 0.2s;
    }
    .glass-tooltip em {
      color: #e11d48;
      font-style: normal;
      font-weight: 600;
      font-size: 18px;
      display: block;
      margin-bottom: 4px;
    }
    .glass-tooltip img {
      margin-top: 8px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }
    .glass-tooltip .tooltip-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
    }
  `;
  document.head.appendChild(style);
}